/**
 * Edit Group page component.
 * Only owners, admins, and moderators can edit the group.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import style from '../styles/EditGroup.module.css';
import GroupBannerUpload from '../components/GroupBannerUpload';
import GroupGalleryUpload from '../components/GroupGalleryUpload';
import GroupProfilePictureUpload from '../components/GroupProfilePictureUpload';
import { useAlertStore } from '../utils/alert';
import { useAuth } from '../contexts/AuthContext';

function EditGroup() {
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const { session, isAuthenticated, isLoading } = useAuth();

  // Form state
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState(false);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Redirect if no groupId
  useEffect(() => {
    if (!groupId) navigate('/dashboard');
  }, [groupId, navigate]);

  // Fetch group info
  const groupQuery = trpc.group.getById.useQuery(
    { id: groupId! },
    { enabled: !!groupId && isAuthenticated },
  );

  // Fetch group members
  const membersQuery = trpc.group.getMembers.useQuery(
    { groupId: groupId! },
    { enabled: !!groupId && isAuthenticated },
  );

  // Fetch current user profile
  const profileQuery = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const updateImages = trpc.group.updateImages.useMutation({
    onSuccess: () => {
      groupQuery.refetch();
    },
  });

  // Determine if user can edit
  const canEdit =
    session &&
    groupQuery.data &&
    profileQuery.data &&
    membersQuery.data &&
    (session.user.id === groupQuery.data.created_by ||
      membersQuery.data.some(
        (m) => m.profile_id === session.user.id && ['admin', 'owner'].includes(m.role),
      ));

  // Populate form
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (groupQuery.data && !initialized) {
      setInitialized(true);
      setGroupName(groupQuery.data.name || '');
      setDescription(groupQuery.data.description || '');
      setPrivacy(groupQuery.data.privacy === 'private');
    }
  }, [groupQuery.data]);

  // Update group mutation (includes banner)
  const utils = trpc.useUtils();
  const updateGroup = trpc.group.update.useMutation({
    onSuccess: () => {
      useAlertStore.getState().showAlert({
        header: 'Success',
        body: 'Group updated successfully!',
        button: 'Ok',
      });
      utils.group.getById.invalidate({ id: groupId! });
      utils.group.listMine.invalidate();

      // Navigate back to the group page
      navigate(`/groups/${groupId}`);
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error updating group',
        button: 'Ok',
      });
    },
  });

  // Submit handler
  const handleGroupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupId || !canEdit) return;

    const updates: {
      groupId: string;
      name?: string;
      description?: string;
      privacy?: 'public' | 'private';
    } = { groupId };

    if (groupName.trim()) updates.name = groupName.trim();
    if (description.trim()) updates.description = description.trim();
    updates.privacy = privacy ? 'private' : 'public';

    updateGroup.mutate(updates);
  };

  // Loading & error handling
  if (!groupId || !session) return null;

  if (groupQuery.isLoading || membersQuery.isLoading) {
    return (
      <div className={style.container}>
        <div className={style.formContainer}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className={style.container}>
        <div className={style.formContainer}>
          <h1>Access Denied</h1>
          <p>You do not have permission to edit this group.</p>
          <button onClick={() => navigate('/dashboard')} className={style.button}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!groupId || groupQuery.error) {
    return (
      <div className={style.container}>
        <div className={style.formContainer}>
          <h1 style={{ color: 'red' }}>Invalid Group ID</h1>
          <button onClick={() => navigate('/dashboard')} className={style.button}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.formContainer}>
        <h1>Edit Group</h1>
        <h2>{groupQuery.data?.name}</h2>

        <form onSubmit={handleGroupSubmit} className={style.form}>
          {/* Name */}
          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="groupName">Group Name</label>
              <input
                id="groupName"
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          {/* Description */}
          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="description">Description</label>
              <input
                id="description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          {/* Privacy */}
          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label className={style.label}>Privacy</label>

              <div className={style.switchWrapper}>
                <span className={!privacy ? style.activeLabel : ''}>Public</span>

                <label className={style.switch}>
                  <input
                    type="checkbox"
                    data-testid="privacy-checkbox"
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                  />
                  <span className={style.slider}></span>
                </label>

                <span className={privacy ? style.activeLabel : ''}>Private</span>
              </div>
            </div>
          </div>

          {/* Profile Picture */}
          <GroupProfilePictureUpload
            groupId={groupId}
            currentUrl={groupQuery.data?.profile_picture_url || ''}
            fallbackLetter={groupQuery.data?.name?.charAt(0)}
            size={80}
            onUploadComplete={(url) => {
              updateImages.mutate({ groupId: groupId, profile_picture_url: url });
            }}
          />

          {/* Banner */}
          <GroupBannerUpload
            groupId={groupId}
            currentUrl={groupQuery.data?.banner_url || ''}
            onUploadComplete={(url) => {
              updateImages.mutate({ groupId: groupId, banner_url: url });
            }}
          />

          {/* Gallery */}
          <GroupGalleryUpload groupId={groupId} />

          {/* Buttons */}
          <div className={style.buttonContainer}>
            <button
              type="button"
              onClick={() => navigate('/group-member/profile')}
              className={`${style.button} ${style.cancelButton}`}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={updateGroup.isPending}
              className={`${style.button} ${style.submitButton}`}
            >
              {updateGroup.isPending ? 'Updating...' : 'Update Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditGroup;
